import * as React from 'react'
import { defineMessages, FormattedMessage, useIntl } from 'react-intl'
import { useFormState } from 'react-use-form-state'
import { useQuery, useMutation } from 'react-apollo'

import { Heading } from '@dooer/react-ui-kit'

import Button from '../../../../components/button'
import { Card, CardBody } from '../../../../components/card'
import WaveContainer from '../../../../components/wave-container'
import { getValidity, Text, Toggle, ErrorText } from '../../../../components/form'
import messages from '../../../../components/functional/basic-signup/form/messages'
import { getEnv, pushGAEvent, normalizePhoneNumber, validatePhoneNumber } from '../../../../lib/utils'

import styles from './form.module.scss'
import { DooerComGetConsultantCategories } from './__generated__/DooerComGetConsultantCategories'
import { DooerComConsultant, DooerComConsultantVariables } from './__generated__/DooerComConsultant'
import { addConsultant, getCategories } from './gql'

interface IFormValues {
  name: string
  email: string
  phone: string
  companyName: string
  city: string
  consultantCategoryIds: string[]
  integrity: boolean
}

const message = defineMessages({
  title: {
    id: 'konsult.form.title',
    defaultMessage: 'Bli Dooer-partner',
  },
  lede: {
    id: 'konsult.form.lede',
    defaultMessage:
      'Vill du vara en del i det nätverk som hjälper vår snabbt växande mängd gratiskunder med ärenden som de inte har möjlighet att hantera själva? Anmäl dig nedan och få möjligheten att bli publicerad som en konsult till Dooers gratistjänster.',
  },
  error: {
    id: 'konsult.form.error',
    defaultMessage: 'Ett okänt fel inträffade. Försök igen eller kontakta supporten.',
  },
  tryAgain: {
    id: 'konsult.form.try-again',
    defaultMessage: 'Försök igen',
  },
  doneTitle: {
    id: 'konsult.form.done-title',
    defaultMessage: 'Allt klart! 🎉',
  },
  doneLede: {
    id: 'konsult.form.done-lede',
    defaultMessage:
      'Vi har nu tagit emot din anmälan och kommer nu granska den manuellt och eventuellt höra av oss till dig innan du publiceras i vår tjänst som en konsult för att hjälpa våra kunder.',
  },

  name: {
    id: 'konsult.form.name',
    defaultMessage: 'Namn',
  },
  city: {
    id: 'konsult.form.city',
    defaultMessage: 'Stad',
  },
  helpLabel: {
    id: 'konsult.form.help-label',
    defaultMessage: 'Kan hjälpa till med',
  },
  cta: {
    id: 'konsult.form.cta',
    defaultMessage: 'Skicka anmälan',
  },
  ctaInfo: {
    id: 'konsult.form.cta-info',
    defaultMessage:
      'Vi går igenom alla anmälningar innan de syns på vår webbsida och förbehåller oss rätten att välja vilka som visas.',
  },
})

export default () => {
  const [submitted, setSubmitted] = React.useState(false)
  const intl = useIntl()
  const { data, error, loading } = useQuery<DooerComGetConsultantCategories>(getCategories)
  const [mutate, state] = useMutation<DooerComConsultant, DooerComConsultantVariables>(addConsultant)
  const [formState, input] = useFormState<IFormValues>(
    {
      consultantCategoryIds: [],
    },
    {
      withIds: true,
    }
  )

  const errorMessages = {
    personnummer: intl.formatMessage(messages.errorPersonnummer),
    email: intl.formatMessage(messages.errorEmail),
    integrity: intl.formatMessage(messages.errorIntegrity),
    termsOfService: intl.formatMessage(messages.errorTermsOfService),
    text: intl.formatMessage(messages.errorText),
    cellPhone: intl.formatMessage(messages.errorCellPhone),
  }
  if (!data) {
    return null
  }

  return (
    <div className={styles.root}>
      <div className={styles.outer}>
        <div className={styles.inner}>
          <div className={styles.root2}>
            <Card className={styles.main}>
              <div className={styles.header}>
                <FormattedMessage {...message.title} tagName="h3" />
                <FormattedMessage {...message.lede} tagName="p" />
              </div>
              <WaveContainer className={styles.wave}>
                <CardBody>
                  <form
                    onSubmit={(e) => {
                      e.preventDefault()
                      if (Object.keys(formState.errors).length > 0) {
                        return false
                      }

                      mutate({
                        variables: {
                          input: {
                            name: formState.values.name,
                            email: formState.values.email,
                            phone: normalizePhoneNumber(formState.values.phone),
                            city: formState.values.city,
                            companyName: formState.values.companyName,
                            consultantCategoryIds: Array.isArray(formState.values.consultantCategoryIds)
                              ? formState.values.consultantCategoryIds
                              : [],
                          },
                        },
                      }).then(() => {
                        if (getEnv() === 'dooer') {
                          pushGAEvent('consultant-lead-added')
                        }
                        setSubmitted(true)
                      })
                    }}
                  >
                    {submitted ? (
                      <div className={styles.done}>
                        <FormattedMessage tagName="h3" {...message.doneTitle} />
                        <FormattedMessage tagName="p" {...message.doneLede} />
                      </div>
                    ) : error ? (
                      <>
                        <ErrorText>
                          <FormattedMessage {...message.error} />
                        </ErrorText>
                        <Button block onClick={() => setSubmitted(false)} type="button" variant="link">
                          <FormattedMessage {...message.tryAgain} />
                        </Button>
                      </>
                    ) : (
                      <>
                        <div className={styles.inputs}>
                          <Text
                            errorCollapse="dynamic"
                            errorMessage={errorMessages.text}
                            input={input}
                            name="name"
                            placeholder={intl.formatMessage(message.name)}
                            required
                            valid={getValidity(formState, 'name')}
                          />
                          <Text
                            errorCollapse="dynamic"
                            errorMessage={errorMessages.email}
                            input={input}
                            name="email"
                            placeholder={intl.formatMessage(messages.email)}
                            required
                            type="email"
                            valid={getValidity(formState, 'email')}
                          />
                          <Text
                            errorCollapse="dynamic"
                            errorMessage={errorMessages.cellPhone}
                            input={input}
                            name="phone"
                            placeholder={intl.formatMessage(messages.cellPhone)}
                            required
                            type="tel"
                            valid={getValidity(formState, 'phone')}
                            validate={(value: string) =>
                              validatePhoneNumber(normalizePhoneNumber(value)) ? true : errorMessages.cellPhone
                            }
                          />
                          <Text
                            errorCollapse="dynamic"
                            errorMessage={errorMessages.text}
                            input={input}
                            name="companyName"
                            placeholder={intl.formatMessage(messages.companyName)}
                            required
                            valid={getValidity(formState, 'companyName')}
                          />
                          <Text
                            errorCollapse="dynamic"
                            errorMessage={errorMessages.text}
                            input={input}
                            name="city"
                            placeholder={intl.formatMessage(message.city)}
                            required
                            valid={getValidity(formState, 'city')}
                          />
                        </div>
                        <div className={styles.toggles}>
                          <Heading level="h2">
                            <FormattedMessage {...message.helpLabel} />
                          </Heading>

                          {data.consultantCategories?.edges.map(({ node }) => (
                            <Toggle
                              checked={
                                Array.isArray(formState.values.consultantCategoryIds)
                                  ? formState.values.consultantCategoryIds.includes(node.id)
                                  : false
                              }
                              errorMessage={errorMessages.integrity}
                              input={input}
                              key={node.id}
                              label={node.name}
                              name="consultantCategoryIds"
                              value={node.id}
                              variant="checkbox"
                            />
                          ))}

                          <br />
                          <Toggle
                            checked={formState.values.integrity}
                            errorMessage={errorMessages.integrity}
                            input={input}
                            label={
                              <>
                                {intl.formatMessage(messages.integrity)}{' '}
                                <a href="/gdpr" target="_blank">
                                  <FormattedMessage {...messages.readMore} />
                                </a>
                              </>
                            }
                            name="integrity"
                            required
                            valid={getValidity(formState, 'integrity')}
                            variant="checkbox"
                          />
                        </div>

                        <Button
                          block
                          disabled={state.loading || loading}
                          loading={state.loading || loading}
                          type="submit"
                          variant="success"
                        >
                          <FormattedMessage {...message.cta} />
                        </Button>
                        <FormattedMessage tagName="p" {...message.ctaInfo} />
                      </>
                    )}
                  </form>
                </CardBody>
              </WaveContainer>
            </Card>
          </div>
        </div>
      </div>
    </div>
  )
}
